import React, { useEffect, useMemo, useState } from 'react';
import {
  Chart,
  LineController,
  LineElement,
  PointElement,
  LinearScale,
  CategoryScale,
  Tooltip,
  Filler,
} from 'chart.js';

import { Line } from 'react-chartjs-2';
import ChartCard from './ChartCard';
import { useChartTheme } from './useChartTheme';
import {
  BRAND_ACCENT_LIGHT,
  BRAND_ACCENT_DARK,
  CARD_SURFACE_LIGHT,
  CARD_SURFACE_DARK,
  TOTAL_LINE_LIGHT,
  TOTAL_LINE_DARK,
} from './constants';
import { deliveryRulesPlugin } from './chartPlugins';
import TimeRangeBrush from './TimeRangeBrush';
import {
  buildClientTimeline,
  formatShortDate,
  formatFullDate,
  TimelineEvent,
} from './trajectoryTimeline';
import { ProjectRemediationSeries } from '../../Models/Retest';

Chart.register(LineController, LineElement, PointElement, LinearScale, CategoryScale, Tooltip, Filler);

interface TotalFindingsChartProps {
  series: ProjectRemediationSeries[];
}

const PRESETS: { label: string; months: number }[] = [
  { label: '3M', months: 3 },
  { label: '6M', months: 6 },
  { label: 'All', months: 0 },
];

/**
 * Resolves a preset to a start index: the first event within `months` of the last one. Always
 * leaves at least two points, since a one-point line is not a line.
 */
export function startIndexForMonths(events: TimelineEvent[], months: number): number {
  const lastIndex = events.length - 1;
  if (months <= 0 || lastIndex < 1) return 0;

  const cutoff = new Date(events[lastIndex].date);
  cutoff.setMonth(cutoff.getMonth() - months);

  const firstInRange = events.findIndex(e => new Date(e.date) >= cutoff);
  if (firstInRange === -1) return 0;
  return Math.min(firstInRange, lastIndex - 1);
}

/**
 * The headline widget: one line for the total open backlog, with a range to scope it.
 *
 * A single series needs no legend — the title names what is plotted, and a one-swatch legend box
 * would only restate it. The current value is the hero figure rather than something the reader has
 * to trace to the right-hand end of the line.
 */
const TotalFindingsChart: React.FC<TotalFindingsChartProps> = ({ series }) => {
  const { textColor, gridColor, isDark } = useChartTheme();
  // Not a severity, so not a severity colour: drawing "all open findings" in Critical red would
  // imply they are all critical, and in High orange that they are all high.
  const lineColor = isDark ? TOTAL_LINE_DARK : TOTAL_LINE_LIGHT;
  const surface = isDark ? CARD_SURFACE_DARK : CARD_SURFACE_LIGHT;
  const accent = isDark ? BRAND_ACCENT_DARK : BRAND_ACCENT_LIGHT;
  const track = isDark ? 'rgba(255,255,255,0.06)' : 'rgba(0,0,0,0.04)';

  const timeline = useMemo(() => buildClientTimeline(series), [series]);
  const lastIndex = Math.max(0, timeline.length - 1);

  const [range, setRange] = useState<{ start: number; end: number }>({ start: 0, end: 0 });
  const [preset, setPreset] = useState<number>(0);

  // The timeline changes whenever the dashboard filter does, so a stale range would point at
  // events that no longer exist. Reset to the full window rather than clamping to a meaningless
  // slice of different data.
  useEffect(() => {
    setRange({ start: 0, end: Math.max(0, timeline.length - 1) });
    setPreset(0);
  }, [timeline]);

  const start = Math.min(range.start, lastIndex);
  const end = Math.min(Math.max(range.end, start), lastIndex);
  const visible = timeline.slice(start, end + 1);

  const applyPreset = (months: number) => {
    setPreset(months);
    setRange({ start: startIndexForMonths(timeline, months), end: lastIndex });
  };

  const deliveryIndices = useMemo(
    () => visible.reduce<number[]>((acc, e, i) => (e.kind === 'delivery' ? [...acc, i] : acc), []),
    [visible]
  );

  const current = visible.length > 0 ? visible[visible.length - 1].total : 0;
  const first = visible.length > 0 ? visible[0].total : 0;
  const delta = current - first;

  const chartData = {
    labels: visible.map(e => formatShortDate(e.date)),
    datasets: [
      {
        label: 'Total open',
        data: visible.map(e => e.total),
        borderColor: lineColor,
        backgroundColor: lineColor,
        pointBackgroundColor: lineColor,
        pointBorderColor: surface,
        pointBorderWidth: 1.5,
        pointRadius: 3.5,
        pointHoverRadius: 5.5,
        borderWidth: 2,
        tension: 0,
        fill: true,
      },
    ],
  };

  const options = {
    responsive: true,
    maintainAspectRatio: false,
    layout: { padding: { top: 8, right: 12 } },
    interaction: { mode: 'index' as const, intersect: false },
    plugins: {
      legend: { display: false },
      tooltip: {
        callbacks: {
          title: (items: any[]) => (visible[items[0]?.dataIndex] ? formatFullDate(visible[items[0].dataIndex].date) : ''),
          beforeBody: (items: any[]) => visible[items[0]?.dataIndex]?.label ?? '',
        },
      },
      deliveryRules: { indices: deliveryIndices, color: gridColor },
      filler: { propagate: false },
    },
    elements: { line: { fill: 'origin' as const } },
    scales: {
      x: {
        type: 'category' as const,
        grid: { display: false },
        ticks: { color: textColor, font: { size: 11 }, maxRotation: 0, autoSkipPadding: 12 },
      },
      y: {
        beginAtZero: true,
        grid: { color: gridColor },
        ticks: { color: textColor, font: { size: 11 }, precision: 0 },
      },
    },
  };

  const deltaLabel =
    delta === 0 ? 'no change in range' : `${delta < 0 ? '−' : '+'}${Math.abs(delta)} in range`;

  const presetButtons = (
    <div className="btn-group btn-group-sm" role="group" aria-label="Time range">
      {PRESETS.map(p => (
        <button
          key={p.label}
          type="button"
          className={`btn btn-sm ${preset === p.months ? 'btn-secondary' : 'btn-outline-secondary'}`}
          style={{ fontSize: 11, paddingBlock: 2 }}
          aria-pressed={preset === p.months}
          onClick={() => applyPreset(p.months)}
        >
          {p.label}
        </button>
      ))}
    </div>
  );

  return (
    <ChartCard
      title="Total open findings"
      sub="All delivered projects · drag the range below to scope the window"
      extra={timeline.length > 1 ? presetButtons : undefined}
    >
      {timeline.length === 0 ? (
        <div className="text-muted text-center py-5 small">
          No delivered projects to show a trajectory for yet
        </div>
      ) : (
        <>
          <div className="d-flex align-items-baseline gap-3 mb-2">
            <span
              data-testid="total-hero"
              style={{ fontSize: '2.4rem', fontWeight: 650, lineHeight: 1, letterSpacing: '-0.02em' }}
            >
              {current}
            </span>
            <span className="text-muted" style={{ fontSize: 12 }}>
              open findings
              <br />
              {/* Bootstrap's own semantic tokens, so the delta follows the app's theme in both
                  modes instead of carrying a colour this component invented. */}
              <span
                data-testid="total-delta"
                style={{
                  fontWeight: 600,
                  color:
                    delta < 0 ? 'var(--bs-success)' : delta > 0 ? 'var(--bs-danger)' : undefined,
                }}
              >
                {deltaLabel}
              </span>
            </span>
          </div>

          <div data-testid="total-plot" style={{ height: 220, width: '100%' }}>
            <Line data={chartData} options={options} plugins={[deliveryRulesPlugin]} />
          </div>

          {timeline.length > 1 && (
            <>
              <TimeRangeBrush
                events={timeline}
                start={start}
                end={end}
                onChange={(s, e) => {
                  setRange({ start: s, end: e });
                  setPreset(-1); // a manual drag is no longer any preset
                }}
                lineColor={textColor}
                accentColor={accent}
                trackColor={track}
              />
              <div className="text-muted text-center" style={{ fontSize: 11, paddingTop: 4 }}>
                {formatFullDate(timeline[start].date)} → {formatFullDate(timeline[end].date)} ·{' '}
                {visible.length} of {timeline.length} tests
              </div>
            </>
          )}
        </>
      )}
    </ChartCard>
  );
};

export default TotalFindingsChart;
